import { phishingLanguageDisplayLabel } from '@/api/enums/phishing-languages.enum';
import { client } from '@/client';
import {
  CampaignPhishingSignEnum,
  CatalogueUsageFilterEnum,
} from '@/client/campaigns';
import { CatalogueFilters } from '@/hooks/useCatalogueFilters';
import { FlexContainer } from '@/ui/styled-ui';
import { getPhishingSignOptions } from '@/utils/helpers';
import { difficultyFilterOptions } from '@/utils/helpers/ui.helper';
import { debounce } from 'lodash';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import React, { SyntheticEvent, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import styled from 'styled-components';

const StyledFilterBar = styled(FlexContainer)`
  padding: var(--default-padding) 0;
  flex-wrap: wrap;
  gap: var(--small-padding);
  width: 100%;

  &::-webkit-scrollbar {
    height: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: #d1d1d1;
    border-radius: 3px;
  }

  .p-input-icon-left {
    min-width: 200px;
    flex: 1 1 auto;
  }

  .p-dropdown {
    flex: 0 0 auto;
    width: auto;
    min-width: fit-content;
    background: transparent;
    border: 1px solid var(--purple-dark) !important;

    &:not(.p-disabled):hover {
      background: var(--red-darkest) !important;
      border-color: var(--red-darkest) !important;

      .p-dropdown-label,
      .p-dropdown-trigger-icon {
        color: var(--white-main);
      }
    }

    .p-dropdown-label {
      background: transparent;
      padding-right: 0;
    }

    &.p-dropdown-clearable .p-dropdown-label {
      padding-right: 1.5rem !important;
    }

    .p-dropdown-trigger {
      background: transparent;
    }
  }

  .p-inputtext {
    border: 2px solid transparent;

    &:enabled:hover {
      border: 2px solid var(--red-darkest);
    }

    &:enabled:focus {
      border: 2px solid var(--red-darkest);
      box-shadow: none;
    }
  }
`;

type CatalogueFilterBarProps = {
  filters: CatalogueFilters;
  onFilterChange: (
    updates: Partial<CatalogueFilters>,
    resetSkip?: boolean,
  ) => void;
};

export const CatalogueFilterBar: React.FC<CatalogueFilterBarProps> = ({
  filters,
  onFilterChange,
}) => {
  const { t } = useTranslation();
  const [searchValue, setSearchValue] = useState('');

  const { data: availableLanguages = [] } = useQuery({
    queryKey: ['campaign-template-languages'],
    queryFn: () => client.campaignTemplates.getCampaignTemplateLanguages(),
  });

  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        onFilterChange({ search: value });
      }, 300),
    [onFilterChange],
  );

  const phishingSignOptions = getPhishingSignOptions(t);

  const languageOptions = useMemo(
    () =>
      availableLanguages
        .map((lang: string) => ({
          label: phishingLanguageDisplayLabel[lang],
          value: lang,
        }))
        .filter((option) => option.label)
        .sort((a, b) => a.label.localeCompare(b.label)),
    [availableLanguages],
  );

  const usageFilterOptions = [
    {
      label: t('campaign.template.showAll'),
      value: CatalogueUsageFilterEnum.SHOW_ALL,
    },
    {
      label: t('campaign.template.showUnused'),
      value: CatalogueUsageFilterEnum.SHOW_UNUSED,
    },
    {
      label: t('campaign.template.showUsed'),
      value: CatalogueUsageFilterEnum.SHOW_USED,
    },
  ];

  return (
    <StyledFilterBar>
      <div className="p-input-icon-left">
        <i className="pi pi-search" />
        <InputText
          className="w-full"
          value={searchValue}
          onInput={(event: SyntheticEvent<HTMLInputElement>) => {
            const value = (event.target as HTMLInputElement).value;
            setSearchValue(value);
            debouncedSearch(value);
          }}
          placeholder={t('templates.searchNameOrSender')}
          autoComplete="off"
        />
      </div>

      <Dropdown
        value={
          filters.usageFilter === CatalogueUsageFilterEnum.SHOW_ALL
            ? null
            : filters.usageFilter
        }
        options={usageFilterOptions}
        onChange={(e) => {
          onFilterChange({
            usageFilter: e.value ?? CatalogueUsageFilterEnum.SHOW_ALL,
          });
        }}
        placeholder={t('campaign.template.showAll')}
        showClear={filters.usageFilter !== CatalogueUsageFilterEnum.SHOW_ALL}
      />

      <Dropdown
        value={filters.phishingSign}
        options={phishingSignOptions}
        onChange={(e) => {
          onFilterChange({ phishingSign: e.value as CampaignPhishingSignEnum });
        }}
        placeholder={t('campaign.template.phishingSign')}
        showClear={!!filters.phishingSign}
      />

      <Dropdown
        value={filters.language ?? null}
        options={languageOptions}
        onChange={(e) => {
          onFilterChange({ language: e.value ?? undefined });
        }}
        placeholder={t('generic.lang')}
        showClear={!!filters.language}
      />

      <Dropdown
        value={filters.difficulty}
        options={difficultyFilterOptions}
        onChange={(e) => {
          onFilterChange({ difficulty: e.value as number });
        }}
        placeholder={t('generic.difficulty')}
        showClear={
          filters.difficulty !== undefined && filters.difficulty !== null
        }
      />
    </StyledFilterBar>
  );
};
